import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useSupabase } from '../../../hooks/useSupabase';

const CreateShiftModal = ({ 
  isOpen, 
  onClose, 
  onSave,
  editingShift = null 
}) => {
  const { supabase } = useSupabase();
  const [shiftData, setShiftData] = useState({
    employeeId: editingShift?.employeeId || '',
    date: editingShift?.date || '',
    startTime: editingShift?.startTime || '',
    endTime: editingShift?.endTime || '',
    department: editingShift?.department || '',
    location: editingShift?.location || '',
    notes: editingShift?.notes || '',
    isRecurring: false,
    recurrencePattern: 'weekly',
    recurrenceEnd: ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // Load employees from database
  useEffect(() => {
    const loadEmployees = async () => {
      if (!isOpen) return;
      
      setLoadingEmployees(true);
      try {
        const { data: employeesData, error } = await supabase
          .from('employees')
          .select(`
            id,
            employee_code,
            name,
            department,
            position,
            is_active
          `)
          .eq('is_active', true)
          .order('name');

        if (error) {
          throw error;
        }

        setEmployees(employeesData || []);
      } catch (err) {
        console.error('Error loading employees:', err);
        setEmployees([]);
      } finally {
        setLoadingEmployees(false);
      }
    };

    loadEmployees();
  }, [isOpen, supabase]);

  // Department field removed as requested

  const locations = [
    'Ragna',
    'Paradise'
  ];

  const handleInputChange = (field, value) => {
    setShiftData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Department field removed as requested
  };

  const validateForm = () => {
    const newErrors = {};

    if (!shiftData?.employeeId) {
      newErrors.employeeId = 'Mitarbeiter ist erforderlich';
    }

    if (!shiftData?.date) {
      newErrors.date = 'Datum ist erforderlich';
    }

    if (!shiftData?.startTime) {
      newErrors.startTime = 'Startzeit ist erforderlich';
    }

    if (!shiftData?.endTime) {
      newErrors.endTime = 'Endzeit ist erforderlich';
    }

    if (shiftData?.startTime && shiftData?.endTime) {
      const start = new Date(`2000-01-01T${shiftData.startTime}`);
      const end = new Date(`2000-01-01T${shiftData.endTime}`);
      
      if (end <= start) {
        newErrors.endTime = 'Endzeit muss nach der Startzeit liegen';
      }
    }

    if (!shiftData?.location) {
      newErrors.location = 'Ort ist erforderlich';
    }

    if (shiftData?.isRecurring && !shiftData?.recurrenceEnd) {
      newErrors.recurrenceEnd = 'Enddatum der Wiederholung ist erforderlich';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const employee = employees?.find(emp => emp?.employee_code === shiftData?.employeeId);
      const newShift = {
        id: editingShift?.id || Date.now(),
        ...shiftData,
        employeeName: employee?.name || '',
        employee_id: employee?.id,
        status: 'scheduled',
        duration: calculateDuration(shiftData?.startTime, shiftData?.endTime)
      };

      if (onSave) {
        onSave(newShift);
      }

      onClose();
    } catch (error) {
      console.error('Error saving shift:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateDuration = (start, end) => {
    if (!start || !end) return '';
    
    const startTime = new Date(`2000-01-01T${start}`);
    const endTime = new Date(`2000-01-01T${end}`);
    const diffMs = endTime - startTime;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffMinutes === 0) {
      return `${diffHours}h`;
    }
    return `${diffHours}h ${diffMinutes}m`;
  };

  const handleClose = () => {
    setShiftData({
      employeeId: '',
      date: '',
      startTime: '',
      endTime: '',
      department: '',
      location: '',
      notes: '',
      isRecurring: false,
      recurrencePattern: 'weekly',
      recurrenceEnd: ''
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Calendar" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-text-primary">
                {editingShift ? 'Schicht bearbeiten' : 'Neue Schicht erstellen'}
              </h2>
              <p className="text-sm text-text-secondary">
                {editingShift ? 'Schichtdetails aktualisieren' : 'Neue Mitarbeiterschicht planen'}
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            iconName="X"
            className="touch-feedback"
          />
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-6">
          {/* Employee Selection */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Mitarbeiter *
            </label>
            <select
              value={shiftData?.employeeId}
              onChange={(e) => handleInputChange('employeeId', e?.target?.value)}
              disabled={loadingEmployees}
              className={`w-full px-3 py-2 border rounded-md bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-ring ${
                errors?.employeeId ? 'border-error' : 'border-border'
              } ${loadingEmployees ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <option value="">
                {loadingEmployees ? 'Lade Mitarbeiter...' : 'Mitarbeiter auswählen'}
              </option>
              {employees?.map((employee) => (
                <option key={employee?.id} value={employee?.employee_code}>
                  {employee?.name} ({employee?.employee_code}) - {employee?.department || 'Allgemein'}
                </option>
              ))}
            </select>
            {errors?.employeeId && (
              <p className="text-sm text-error mt-1">{errors?.employeeId}</p>
            )}
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              type="date"
              label="Datum *"
              value={shiftData?.date}
              onChange={(e) => handleInputChange('date', e?.target?.value)}
              error={errors?.date}
              required
            />
            
            <Input
              type="time"
              label="Startzeit *"
              value={shiftData?.startTime}
              onChange={(e) => handleInputChange('startTime', e?.target?.value)}
              error={errors?.startTime}
              required
            />
            
            <Input
              type="time"
              label="Endzeit *"
              value={shiftData?.endTime}
              onChange={(e) => handleInputChange('endTime', e?.target?.value)}
              error={errors?.endTime}
              required
            />
          </div>

          {/* Duration Display */}
          {shiftData?.startTime && shiftData?.endTime && (
            <div className="bg-muted/30 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} className="text-text-secondary" />
                <span className="text-sm text-text-secondary">Duration:</span>
                <span className="text-sm font-medium text-text-primary">
                  {calculateDuration(shiftData?.startTime, shiftData?.endTime)}
                </span>
              </div>
            </div>
          )}

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Location *
            </label>
            <select
              value={shiftData?.location}
              onChange={(e) => handleInputChange('location', e?.target?.value)}
              className={`w-full px-3 py-2 border rounded-md bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-ring ${
                errors?.location ? 'border-error' : 'border-border'
              }`}
            >
              <option value="">Select location</option>
              {locations?.map((location) => (
                <option key={location} value={location}>
                  {location}
                </option>
              ))}
            </select>
            {errors?.location && (
              <p className="text-sm text-error mt-1">{errors?.location}</p>
            )}
          </div>

          {/* Recurring Shift Options */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isRecurring"
                checked={shiftData?.isRecurring}
                onChange={(e) => handleInputChange('isRecurring', e?.target?.checked)}
                className="rounded border-border"
              />
              <label htmlFor="isRecurring" className="text-sm font-medium text-text-primary">
                Create recurring shift
              </label>
            </div>

            {shiftData?.isRecurring && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Repeat Pattern
                  </label>
                  <select
                    value={shiftData?.recurrencePattern}
                    onChange={(e) => handleInputChange('recurrencePattern', e?.target?.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                
                <Input
                  type="date"
                  label="Ende der Wiederholung *"
                  value={shiftData?.recurrenceEnd}
                  onChange={(e) => handleInputChange('recurrenceEnd', e?.target?.value)}
                  error={errors?.recurrenceEnd}
                  required={shiftData?.isRecurring}
                />
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Notizen
            </label>
            <textarea
              value={shiftData?.notes}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
              placeholder="Zusätzliche Notizen oder Anweisungen hinzufügen..."
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-md bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
            className="touch-feedback"
          >
            Abbrechen
          </Button>
          
          <Button
            variant="default"
            onClick={handleSave}
            loading={isLoading}
            iconName="Save"
            iconPosition="left"
            className="touch-feedback"
          >
            {isLoading ? 'Speichere...' : editingShift ? 'Schicht aktualisieren' : 'Schicht erstellen'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateShiftModal;